import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { HeroFeedItem as HeroFeedItemType } from '#src/app/pages/psf/promoteOrShare/pageContext';

const AVATAR_SIZE = 32;
const CONTAINER_PADDING = 24;
const GAP_BETWEEN_TEXTS = 4;

const useContainerWidth = (isDesktopMode: boolean) => {
	const [availableWidth, setAvailableWidth] = useState<number>(0);
	const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);

	const handleSetContainerRef: (element: HTMLElement | null) => void = useCallback((element: HTMLElement | null) => {
		setContainerElement(element);
	}, []);

	useEffect(() => {
		if (isDesktopMode || !containerElement || typeof ResizeObserver === 'undefined') return undefined;

		const measureWidth = () => {
			const containerWidth = containerElement.offsetWidth || 280;
			const textWidth = containerWidth - AVATAR_SIZE - CONTAINER_PADDING;
			setAvailableWidth(textWidth);
		};

		measureWidth();

		const resizeObserver = new ResizeObserver(measureWidth);
		resizeObserver.observe(containerElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [isDesktopMode, containerElement]);

	return { availableWidth, setContainerRef: handleSetContainerRef };
};

const getTranslatedTexts = (
	item: HeroFeedItemType,
	translate: (key: string, replacements: Record<string, string>) => string,
) => {
	if (item.event.__typename === 'HeroFeedPromotionEvent') {
		const metaAltTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.promote_activity_text', {
			promoter_name: item.userDisplayName,
			amount_formatted: item.event.amountLocalizedWithCurrency ?? '',
		});

		const metaTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.promote_activity_text', {
			promoter_name: '',
			amount_formatted: item.event.amountLocalizedWithCurrency ?? '',
		});

		return { metaAltTextHtml, metaTextHtml };
	}

	const metaAltTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.share_activity_text', {
		sharer_name: item.userDisplayName,
	});

	const metaTextHtml = translate('fe.components.psf.promote_or_share.combo_feed.share_activity_text', {
		sharer_name: '',
	});

	return { metaAltTextHtml, metaTextHtml };
};

/**
 * Measures precise pixel width of text using canvas 2D context.
 *
 * Why canvas instead of CSS text-overflow: ellipsis?
 * - LabeledAvatar component is immutable - we must pre-truncate with "..." before passing text
 * - Need exact 30%/70% width allocation between name and description (requires pixel-perfect measurement)
 * - CSS ellipsis would require modifying LabeledAvatar's internal DOM structure
 * - Canvas provides accurate font metrics (kerning, proportional spacing) without DOM manipulation
 */
export const measureTextWidth = (text: string, fontSize: string = '12px', fontWeight: string = 'normal'): number => {
	if (typeof document === 'undefined') return text.length * 8;

	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) return text.length * 8;

	context.font = `${fontWeight} ${fontSize} -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
	return context.measureText(text).width;
};

const truncateToWidth = (text: string, maxWidth: number, fontSize: string, fontWeight: string): string => {
	let truncated = text;
	let currentWidth = measureTextWidth(text, fontSize, fontWeight);

	while (currentWidth > maxWidth && truncated.length > 3) {
		truncated = truncated.slice(0, -1);
		currentWidth = measureTextWidth(`${truncated}...`, fontSize, fontWeight);
	}

	return truncated.length < text.length ? `${truncated}...` : text;
};

/**
 * Truncates name and description to fit available width.
 * Allocates 30% to name, 70% to description to prioritize showing the action.
 */
export const smartTruncateByWidth = (
	name: string,
	description: string,
	availableWidth: number,
): { name: string; description: string } => {
	const nameWidth = measureTextWidth(name, '12px', 'bold');
	const descWidth = measureTextWidth(description, '12px', 'normal');
	const totalWidth = nameWidth + descWidth + GAP_BETWEEN_TEXTS;

	if (totalWidth <= availableWidth) {
		return { name, description };
	}

	const nameMaxWidth = (availableWidth - GAP_BETWEEN_TEXTS) * 0.3;
	const descMaxWidth = (availableWidth - GAP_BETWEEN_TEXTS) * 0.7;

	return {
		name: truncateToWidth(name, nameMaxWidth, '12px', 'bold'),
		description: truncateToWidth(description, descMaxWidth, '12px', 'normal'),
	};
};

export function useHeroFeedItem(
	item: HeroFeedItemType,
	isDesktopMode: boolean = false,
): {
	data: {
		altText: string;
		metaText: string;
		displayName: string;
		displayMetaText: string;
	};
	actions: {
		setContainerRef: (element: HTMLElement | null) => void;
	};
} {
	const { translate } = useI18n();
	const { availableWidth, setContainerRef } = useContainerWidth(isDesktopMode);

	const memoResult = useMemo(() => getTranslatedTexts(item, translate), [item, translate]);

	const altText = memoResult.metaAltTextHtml.replace(/<[^>]+>/g, '');
	const metaText = memoResult.metaTextHtml.replace(/<[^>]+>/g, '');

	const { name: displayName, description: displayMetaText } = useMemo<{ name: string; description: string }>(() => {
		if (isDesktopMode || availableWidth === 0) {
			return { name: item.userDisplayName, description: metaText };
		}

		return smartTruncateByWidth(item.userDisplayName, metaText, availableWidth);
	}, [isDesktopMode, item.userDisplayName, metaText, availableWidth]);

	return {
		data: {
			altText,
			metaText,
			displayName,
			displayMetaText,
		},
		actions: {
			setContainerRef,
		},
	};
}
